import React from "react";
import {
  I2cButton,
  I2cDialog,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "./ConfirmationModal.scss";

/**
 * ConfirmationModal component displays a dialog with a confirmation message.
 *
 * @component
 * @param {Object} props - The properties of the ConfirmationModal component.
 * @param {string} [props.message=""] - The confirmation message to be displayed.
 * @param {string} [props.title="Are you sure"] - The title of the confirmation dialog.
 * @param {string} [props.headerArt="linear-tick-circle"] - The art or icon to be displayed in the dialog header.
 * @param {string} [props.layout="layoutCenter"] - The layout style of the dialog.
 * @param {string} [props.confirmBtnVariant="primary"] - The variant style of the confirmation button.
 * @param {Function} [props.handleConfirmation=() => {}] - The callback function to be executed when the user confirms.
 * @param {boolean} [props.open=false] - Controls whether the dialog is open or closed.
 * @param {string} [props.variant="primary"] - The variant style of the dialog.
 * @param {Function} [props.setOpen=() => {}] - Function to set the state of the dialog's open/closed status.
 * @param {React.ReactNode} [props.children] - Additional children components to be rendered inside the dialog.
 * @param {string} [props.disableConfirmBtn=false] - If true, disables the confirmation button.
 * @param {boolean} [props.cancelButtonText=Close] - If not passed. Default text will be Close
 * @param {boolean} [props.confirmButtonText=Confirm] - If not passed. Default text will be Confirm
 * @param {boolean} [props.disableCancelBtn=false] - If true, disables the cancel button.
 * @returns {JSX.Element} - Rendered ConfirmationModal component.
 */
const ConfirmaionModal = ({
  message = "",
  title = "Are you sure",
  headerArt = "linear-tick-circle",
  layout = "layoutCenter",
  confirmBtnVariant = "primary",
  handleConfirmation = () => {},
  open = false,
  variant = "primary",
  setOpen = () => {},
  children,
  disableConfirmBtn = false,
  cancelButtonText = "Close",
  confirmButtonText,
  disableCancelBtn = false,
  destructive = false,
  confirmButtonLoading = false,
  closeButtonSize = "medium",
  parentClass = "",
}) => {
  return (
    <>
      {/* I2cDialog component for confirmation dialog */}
      <I2cDialog
        className={`custom-dialog dialog2 ${parentClass}`}
        label={title}
        headerArt={headerArt}
        layout={layout}
        open={open}
        style={{ zIndex: 99999999 }}
        headerArtStyle="shadowed"
        variant={variant}
        onI2cHide={(event) => {
          // Close the dialog when clicking outside its content
          if (event.target.tagName === "I2C-DIALOG") setOpen(false);
        }}
        closable
      >
        {message && <p>{message}</p>}
        {/* Footer with Close and Confirm buttons */}
        <footer slot="footer" className="confirmation-modal-footer">
          {/* Close Button */}
          <I2cButton
            slot="footer"
            variant="secondary gray"
            onClick={() => setOpen(false)}
            disabled={disableCancelBtn}
            size={closeButtonSize}
          >
            {cancelButtonText}
          </I2cButton>
          {/* Confirm Button */}
          {confirmButtonText && (
            <I2cButton
              // Remove this desctructive property to change the color to blue
              destructive={destructive}
              onClick={handleConfirmation}
              slot="footer"
              variant={confirmBtnVariant}
              disabled={disableConfirmBtn}
              loading={confirmButtonLoading}
            >
              {confirmButtonText}
            </I2cButton>
          )}
        </footer>
      </I2cDialog>
      {/* Additional children */}
      {children}
    </>
  );
};

export default ConfirmaionModal;
